import { useMemo } from 'react';
import {
  addDays,
  formatHeureMur,
  fromIsoDate,
  toIsoDate,
} from '@/lib/dates';
import type { AvailableSlot } from '@/hooks/useSlots';
import { findHoliday, type Holiday } from '@/hooks/useHolidays';
import type { Mechanic } from '@/lib/types';

const DAY_LABELS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];

type Props = {
  weekStart: string; // ISO date — Monday of displayed week
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTodayWeek: () => void;
  slots: AvailableSlot[];
  mecanos: Mechanic[];
  selectedSlot: AvailableSlot | null;
  onPickSlot: (slot: AvailableSlot) => void;
  closedDays: number[]; // 0=Sunday, 1=Monday…
  holidays?: Holiday[]; // fériés signalés (couleur), jamais bloquants
  isLoading: boolean;
};

/**
 * Vue Semaine du picker de créneau dans la modal Nouveau RDV (CDC §4.2 D).
 * 7 colonnes (lundi → dimanche), chaque colonne liste les créneaux disponibles du jour.
 * Backend renvoie 1 créneau par mécano par jour (cf. findAvailableSlots) — suffisant
 * pour donner une vue d'ensemble cliquable. La sélection se fait au niveau du créneau.
 */
export default function WeekCalendar({
  weekStart,
  onPrevWeek,
  onNextWeek,
  onTodayWeek,
  slots,
  mecanos,
  selectedSlot,
  onPickSlot,
  closedDays,
  holidays,
  isLoading,
}: Props) {
  const days = useMemo(() => {
    const start = fromIsoDate(weekStart);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [weekStart]);

  // Groupe les créneaux par date heure-mur (yyyy-mm-dd UTC, finding 26).
  // slot.debut est stocké en composants UTC = heure mur ; on lit directement
  // les 10 premiers caractères de l'ISO plutôt que de passer par un Date local
  // (qui décalerait le jour selon le fuseau du device). Cohérent avec
  // PlanningWeekView qui indexe par dateRdv.slice(0,10).
  const slotsByDay = useMemo(() => {
    const map = new Map<string, AvailableSlot[]>();
    for (const slot of slots) {
      const key = slot.debut.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    // Tri par heure de début dans chaque jour
    for (const [, list] of map) {
      list.sort((a, b) => a.debut.localeCompare(b.debut));
    }
    return map;
  }, [slots]);

  const today = toIsoDate(new Date());
  const endDate = days[6]!;
  const startMonth = days[0]!.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const endMonth = endDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const headerLabel = startMonth === endMonth ? startMonth : `${startMonth} – ${endMonth}`;

  return (
    <div className="space-y-3">
      {/* Header navigation */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-text-primary capitalize">{headerLabel}</h4>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onPrevWeek}
            className="h-8 w-8 flex items-center justify-center border border-border-light rounded-md text-text-secondary hover:bg-surface-hover"
            aria-label="Semaine précédente"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={onTodayWeek}
            className="h-8 px-2 text-xs font-medium border border-border-light rounded-md text-text-secondary hover:bg-surface-hover"
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={onNextWeek}
            className="h-8 w-8 flex items-center justify-center border border-border-light rounded-md text-text-secondary hover:bg-surface-hover"
            aria-label="Semaine suivante"
          >
            ›
          </button>
        </div>
      </div>

      {/* Week grid */}
      <div className="grid grid-cols-7 gap-px bg-border-light border border-border-light rounded-md overflow-hidden">
        {days.map((day, idx) => {
          const iso = toIsoDate(day);
          const isToday = iso === today;
          const dow = day.getDay();
          const isClosed = closedDays.includes(dow);
          const isPast = iso < today;
          const holiday = !isClosed && !isPast ? findHoliday(holidays, iso) : undefined;
          const daySlots = slotsByDay.get(iso) ?? [];

          return (
            <div
              key={iso}
              className={`flex flex-col min-h-[180px] ${
                isClosed || isPast ? 'bg-surface-bg' : 'bg-surface-white'
              }`}
            >
              {/* Header jour */}
              <div
                className={`px-2 py-1.5 border-b border-border-light text-center ${
                  isToday ? 'bg-ktm-orange-light' : holiday ? 'bg-status-attente-pieces-bg' : ''
                }`}
              >
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${
                  isToday ? 'text-ktm-orange' : 'text-text-muted'
                }`}>
                  {DAY_LABELS[idx]}
                </div>
                <div className={`text-sm font-semibold ${
                  isToday ? 'text-ktm-orange' : isPast || isClosed ? 'text-text-disabled' : 'text-text-primary'
                }`}>
                  {day.getDate()}
                </div>
                {holiday && (
                  <div
                    className="text-[9px] font-medium text-status-attente-pieces-text truncate"
                    title={holiday.nom}
                  >
                    Férié
                  </div>
                )}
              </div>

              {/* Contenu jour */}
              <div className="flex-1 p-1.5 flex flex-col gap-1">
                {isClosed ? (
                  <div className="text-[10px] text-text-disabled text-center pt-3">Fermé</div>
                ) : isPast ? (
                  <div className="text-[10px] text-text-disabled text-center pt-3">—</div>
                ) : isLoading ? (
                  <div className="text-[10px] text-text-muted text-center pt-3">…</div>
                ) : daySlots.length === 0 ? (
                  <div className="text-[10px] text-text-muted text-center pt-3">Complet</div>
                ) : (
                  daySlots.map((slot, sidx) => {
                    const mecano = mecanos.find((m) => m.id === slot.mecanicienId);
                    const isSelected =
                      selectedSlot?.debut === slot.debut &&
                      selectedSlot?.mecanicienId === slot.mecanicienId;
                    return (
                      <button
                        key={`${slot.debut}-${slot.mecanicienId}-${sidx}`}
                        type="button"
                        onClick={() => onPickSlot(slot)}
                        className={`w-full px-1.5 py-1 rounded-sm text-[11px] flex items-center gap-1 transition-colors ${
                          isSelected
                            ? 'bg-ktm-orange text-text-on-orange border border-ktm-orange'
                            : 'bg-surface-hover hover:bg-ktm-orange-light border border-border-light text-text-primary'
                        }`}
                      >
                        {mecano && (
                          <span
                            className="w-2 h-2 rounded-sm shrink-0"
                            style={{ backgroundColor: mecano.couleur }}
                            aria-hidden
                          />
                        )}
                        <span className="font-mono font-medium truncate">
                          {formatHeureMur(slot.debut)}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div className="flex items-center gap-3 text-[10px] text-text-muted">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-surface-hover border border-border-light" />
          Disponible
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-ktm-orange" />
          Sélectionné
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-surface-bg border border-border-light" />
          Fermé / Passé
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-status-attente-pieces-bg" />
          Férié (ouvert)
        </span>
      </div>
    </div>
  );
}
